import {
  useCallback,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { isEqual } from "lodash";
import {
  Dropdown,
  DropdownButton,
  OverlayTrigger,
  InputGroup,
  FormControl,
  Button,
  Tooltip,
} from "react-bootstrap";
import messages from "messages";
// components
import Checkbox from "components/checkbox/Checkbox";
import PaneLayout from "../pane-layout";
import { ReactComponent as ChevronDown } from "assets/svg/outline-chevron-down-16.svg";
import { ReactComponent as OutlineInfoCircle } from "assets/svg/outline-info-circle.svg";
// styles
import styles from "./styles.module.scss";
// hooks
import { useOrchidPro } from "components/Dashboard/DoctorDashboard/contexts/orchid-pro-context";
// types
import type { PaneProps, PaneRefProps } from "../../MainSetup";
// services
import postOrchidPro from "services/postOrchidPro";
// utils
import getClasses from "utils/getClasses";
import { isInvalidCancellationPolicy } from "./utils/isInvalidCancellationPolicy";

/**
 * get menu options for hours
 */
function getHourOption(hours: number) {
  return `Less than ${hours} hours`;
}
const HOUR_OPTIONS = [24, 48, 72, 96] as const;

// https://www.figma.com/file/YQmVxcqfIiUzotzmuNzezZ/Orchid_Doctors_Desktop?node-id=1014%3A11451
const FEE_TYPE_OPTIONS = Object.freeze({
  FLAT: { id: "FLAT", stringKey: "flatFee" },
  PERCENT: { id: "PERCENT", stringKey: "sessionRatePercentage" },
} as const);

/**
 * default average cancellation for orchid pro
 */
const DEFAULT_POLICY = Object.freeze({
  FEE_TYPE: FEE_TYPE_OPTIONS.PERCENT.id,
  VALUE: 20,
  HOURS: 24,
});

const ForwardedCancellationPolicyPane = forwardRef<PaneRefProps, PaneProps>(
  function CancellationPolicyPane({ onShowModal, onDisabled, ...rest }, ref) {
    const { orchidPro, setOrchidPro } = useOrchidPro();
    const [checked, setChecked] = useState(false);

    const [hours, setHours] = useState<number | undefined>(
      DEFAULT_POLICY.HOURS,
    );
    const [feeType, setFeeType] = useState<
      keyof typeof FEE_TYPE_OPTIONS | null
    >(null);
    const [value, setValue] = useState<number | null>(null);
    const [isInvalid, setIsInvalid] = useState(false);

    const handleSetValue = useCallback((nextValue: number | null) => {
      setIsInvalid(false);
      setValue(nextValue);
    }, []);

    const handleApplyDefault = () => {
      setChecked(false);
      setHours(DEFAULT_POLICY.HOURS);
      setFeeType(DEFAULT_POLICY.FEE_TYPE);
      handleSetValue(DEFAULT_POLICY.VALUE);
    };

    const syncData = useCallback(() => {
      const {
        cancellation_notice_hours,
        cancellation_policy_flat_fee,
        cancellation_policy_percent_fee,
      } = orchidPro || {};
      const nextChecked =
        (cancellation_notice_hours === 0 &&
          cancellation_policy_flat_fee === null &&
          cancellation_policy_percent_fee === null) ||
        (cancellation_notice_hours === null &&
          cancellation_policy_flat_fee === null &&
          cancellation_policy_percent_fee === null);
      setChecked(nextChecked);
      setHours(
        nextChecked ? 0 : cancellation_notice_hours || DEFAULT_POLICY.HOURS,
      );
      setFeeType(
        cancellation_policy_percent_fee !== undefined &&
          cancellation_policy_percent_fee !== null
          ? FEE_TYPE_OPTIONS.PERCENT.id
          : FEE_TYPE_OPTIONS.FLAT.id,
      );
      handleSetValue(
        (cancellation_policy_flat_fee || cancellation_policy_percent_fee) ??
          null,
      );
    }, [handleSetValue, orchidPro]);

    // sync data from api
    useEffect(() => {
      syncData();
    }, [syncData]);

    useEffect(() => {
      if (!checked && !value && !hours) {
        setFeeType(DEFAULT_POLICY.FEE_TYPE);
        setHours(DEFAULT_POLICY.HOURS);
        setValue(DEFAULT_POLICY.VALUE);
      }
    }, [checked, hours, value]);

    const inputRef = useRef<HTMLInputElement | null>(null);
    // const isInvalid = useMemo(
    //   () =>
    //     checked ||
    //     (hours !== null &&
    //       hours !== undefined &&
    //       value !== null &&
    //       value !== undefined),
    //   [checked, hours, value],
    // );

    const handleApply = useCallback(async () => {
      setIsInvalid(false);
      if (!checked) {
        if (!inputRef.current!.checkValidity()) {
          setIsInvalid(true);
          throw Error("");
        }
      }

      const cancellationPolicy = checked
        ? {
            clear_cancellation_policy: true,
            cancellation_notice_hours: null,
            cancellation_policy_flat_fee: null,
            cancellation_policy_percent_fee: null,
          }
        : {
            clear_cancellation_policy: false,
            cancellation_notice_hours: hours,
            ...(feeType === FEE_TYPE_OPTIONS.FLAT.id
              ? {
                  cancellation_policy_flat_fee: value,
                  cancellation_policy_percent_fee: null,
                }
              : {
                  cancellation_policy_flat_fee: null,
                  cancellation_policy_percent_fee: value,
                }),
          };

      const isChanged = !isEqual(cancellationPolicy, {
        cancellation_notice_hours: orchidPro?.cancellation_notice_hours,
        cancellation_policy_flat_fee: orchidPro?.cancellation_policy_flat_fee,
        cancellation_policy_percent_fee:
          orchidPro?.cancellation_policy_percent_fee,
      });

      if (!isChanged) {
        return;
      }
      const nextOrchidPro = await postOrchidPro({
        ...orchidPro,
        ...cancellationPolicy,
      });
      setOrchidPro(nextOrchidPro);
    }, [checked, feeType, hours, orchidPro, setOrchidPro, value]);

    useImperativeHandle(ref, () => ({
      handleApply,
    }));

    useEffect(() => {
      if (onDisabled) {
        onDisabled(
          isInvalidCancellationPolicy({
            checked,
            hours,
            value,
          }),
        );
      }
    }, [checked, hours, onDisabled, value]);

    return (
      <PaneLayout
        Title={messages.setCancellationPolicy}
        Subtitle={messages.sometimesYourPatients}
        {...rest}
      >
        <div className={styles["cancellation-policy-pane__options"]}>
          <div
            className={styles["cancellation-policy-pane__cancellation-notice"]}
          >
            <label
              htmlFor="fee-type-option"
              className={styles["cancellation-policy-pane__label"]}
            >
              <span className="mr-1">{messages.cancellationNotice}</span>
              <OverlayTrigger
                placement={"top"}
                overlay={
                  <Tooltip
                    id="my-cancellation-notice-tooltip"
                    className={styles["cancellation-policy-pane__tooltip"]}
                  >
                    {messages.ifAPatientCancelsWithin}
                  </Tooltip>
                }
              >
                <OutlineInfoCircle
                  className={styles["cancellation-policy-pane__icon"]}
                />
              </OverlayTrigger>
            </label>

            <DropdownButton
              disabled={checked}
              id="fee-type-option"
              className={getClasses(
                styles["cancellation-policy-pane__dropdown-button"],
                styles["cancellation-policy-pane__dropdown-button--hour"],
                "w-100",
              )}
              variant="outline-secondary"
              style={{
                color: !hours ? "var(--light-gray)" : "inherit",
              }}
              title={
                <span
                  className={getClasses(
                    styles[
                      "cancellation-policy-pane__dropdown-button-label-container"
                    ],
                    checked &&
                      styles[
                        "cancellation-policy-pane__dropdown-button-label-container--disabled"
                      ],
                  )}
                >
                  <span
                    className={
                      styles["cancellation-policy-pane__dropdown-button-label"]
                    }
                  >
                    {!hours ? messages.selectAnOption : getHourOption(hours)}
                  </span>

                  <ChevronDown className="ml-auto" />
                </span>
              }
            >
              {HOUR_OPTIONS.map((el) => (
                <Dropdown.Item
                  className="rg-14"
                  key={el}
                  onClick={() => setHours(el)}
                >
                  {getHourOption(el)}
                </Dropdown.Item>
              ))}
            </DropdownButton>
          </div>
          <div className={styles["cancellation-policy-pane__cancellation-fee"]}>
            <label
              htmlFor="fee-option"
              className={styles["cancellation-policy-pane__label"]}
            >
              {messages.cancellationFee}
            </label>
            <InputGroup className="w-100 rg-14" id="fee-option">
              <DropdownButton
                disabled={checked}
                className={getClasses(
                  styles["cancellation-policy-pane__dropdown-button"],
                  styles["cancellation-policy-pane__dropdown-button--type"],
                  isInvalid &&
                    styles[
                      "cancellation-policy-pane__dropdown-button--type--invalid"
                    ],
                )}
                variant="outline-secondary"
                as="div"
                title={
                  <span
                    className={getClasses(
                      styles[
                        "cancellation-policy-pane__dropdown-button-label-container"
                      ],
                      checked &&
                        styles[
                          "cancellation-policy-pane__dropdown-button-label-container--disabled"
                        ],
                    )}
                  >
                    <span
                      className={
                        styles[
                          "cancellation-policy-pane__dropdown-button-label"
                        ]
                      }
                    >
                      {!feeType
                        ? messages.selectAnOption
                        : messages[FEE_TYPE_OPTIONS[feeType].stringKey]}
                    </span>
                    <ChevronDown className="ml-auto" />
                  </span>
                }
                id="input-group-dropdown-1"
              >
                <Dropdown.Item
                  className="rg-14"
                  onClick={() => {
                    if (feeType !== FEE_TYPE_OPTIONS.FLAT.id) {
                      setFeeType(FEE_TYPE_OPTIONS.FLAT.id);
                      handleSetValue(10);
                    }
                  }}
                >
                  {messages.flatFee}
                </Dropdown.Item>
                <Dropdown.Item
                  className="rg-14"
                  onClick={() => {
                    if (feeType !== FEE_TYPE_OPTIONS.PERCENT.id) {
                      setFeeType(FEE_TYPE_OPTIONS.PERCENT.id);
                      handleSetValue(20);
                    }
                  }}
                >
                  {messages.sessionRatePercentage}
                </Dropdown.Item>
              </DropdownButton>
              {feeType === FEE_TYPE_OPTIONS.FLAT.id ? (
                <InputGroup.Text
                  className={getClasses(
                    styles["cancellation-policy-pane__addon"],
                    styles["cancellation-policy-pane__addon-text"],
                    styles["cancellation-policy-pane__addon-text--prepend"],
                    checked &&
                      styles[
                        "cancellation-policy-pane__addon-text--disabled"
                      ],
                    isInvalid &&
                      styles["cancellation-policy-pane__addon-text--invalid"],
                  )}
                  id="fee-type-unit"
                >
                  {"$"}
                </InputGroup.Text>
              ) : null}
              <FormControl
                {...(!checked && { required: true })}
                ref={inputRef}
                isInvalid={isInvalid}
                disabled={checked}
                className={styles["cancellation-policy-pane__input"]}
                aria-describedby="fee-type-unit"
                type="number"
                min={0}
                max={feeType === FEE_TYPE_OPTIONS.FLAT.id ? 10000 : 100}
                value={value ?? ""}
                onKeyDown={(e) => {
                  if (["-", "+"].includes(e.key)) {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                  }
                }}
                onChange={(e) => {
                  if (e.target.value === "") {
                    return handleSetValue(null);
                  }
                  handleSetValue(+e.target.value);
                }}
                placeholder={"10.00"}
              />
              {feeType === FEE_TYPE_OPTIONS.PERCENT.id && (
                <InputGroup.Text
                  className={getClasses(
                    styles["cancellation-policy-pane__addon"],
                    styles["cancellation-policy-pane__addon-text"],
                    styles["cancellation-policy-pane__addon-text--append"],
                    checked &&
                      styles[
                        "cancellation-policy-pane__addon-text--disabled"
                      ],
                    isInvalid &&
                      styles["cancellation-policy-pane__addon-text--invalid"],
                  )}
                  id="fee-type-unit"
                >
                  {"%"}
                </InputGroup.Text>
              )}
            </InputGroup>
          </div>

          {/* apply default button */}
          <Button
            disabled={checked}
            variant={"link"}
            className={styles["cancellation-policy-pane__default-btn"]}
            onClick={handleApplyDefault}
          >
            {messages.setToDefault}
          </Button>
        </div>

        <Checkbox
          id="add-policy"
          label={messages.noCancellationPolicy}
          checked={checked}
          onChange={(e) => {
            setChecked(e.target.checked);
            if (e.target.checked) {
              setIsInvalid(false);
            }
          }}
          className={styles["cancellation-policy-pane__checkbox"]}
        />
      </PaneLayout>
    );
  },
);
export default ForwardedCancellationPolicyPane;
